import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridEventListener } from '@mui/x-data-grid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { facultyApi } from '../../../services';
import { useErrorHandler } from '../../../lib/errorHandler';
import { FacultySchema } from '../../../services/facultyApi';
import { z } from 'zod';
import { MOBILE_BREAKPOINT, ROW_HEIGHTS, LIST_HEIGHTS } from '../../../utils/responsive';
import { useEntityListState, createEntityToolbar, type StatsQueryParams } from '../../../hooks';

type Faculty = z.infer<typeof FacultySchema>;
import { BaseDataGrid, PageHeader, FacultyDialog, DeleteConfirmDialog, PersistentTabs, TabConfig } from '../../../components';
import { FacultiesChart } from './FacultiesChart';

const FacultiesToolbar = createEntityToolbar({
	searchPlaceholder: 'Tìm kiếm khoa...',
	exportFn: (params) => facultyApi.exportToExcel(params),
});
import { useFacultiesColumns, useFacultiesQuery } from '../hooks';

interface FacultiesContentProps {
	isAdmin: boolean;
	canEdit: boolean;
}

export default function FacultiesContent({ isAdmin, canEdit }: FacultiesContentProps) {
	const { showError, showSuccess } = useErrorHandler();
	const queryClient = useQueryClient();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
	const [searchParams, setSearchParams] = useSearchParams();

	const {
		searchValue, paginationModel, sortModel, filterModel,
		handlePaginationModelChange, handleSortModelChange,
		handleFilterModelChange, handleSearch,
	} = useEntityListState({ defaultSortField: 'tenKhoa' });

	// Dialog states
	const [facultyDialog, setFacultyDialog] = useState<{
		open: boolean;
		mode: 'create' | 'edit' | 'view';
		faculty: Faculty | null;
	}>({ open: false, mode: 'create', faculty: null });
	const dialogRef = useRef(facultyDialog);
	dialogRef.current = facultyDialog;

	const [deleteDialog, setDeleteDialog] = useState<{
		open: boolean;
		faculty: Faculty | null;
	}>({ open: false, faculty: null });

	// Callback functions for columns
	const handleView = useCallback(
		(faculty: Faculty) => () => {
			setSearchParams({ action: 'view', id: faculty.maKhoa });
		},
		[setSearchParams],
	);

	const handleEdit = useCallback(
		(faculty: Faculty) => () => {
			setSearchParams({ action: 'edit', id: faculty.maKhoa });
		},
		[setSearchParams],
	);

	const handleDelete = useCallback(
		(faculty: Faculty) => () => {
			setDeleteDialog({
				open: true,
				faculty,
			});
		},
		[],
	);

	// Use query hook with pagination and sorting
	const queryParams: StatsQueryParams = {
		filters: { query: searchValue },
		page: paginationModel.page,
		pageSize: paginationModel.pageSize,
		sortBy: sortModel.length > 0 ? sortModel[0].field : undefined,
		sortOrder: (sortModel.length > 0 ? sortModel[0].sort : undefined) as 'asc' | 'desc' | undefined,
	};
	const { data: facultiesData, isLoading, refetch } = useFacultiesQuery(queryParams);
	const exportParams = useFacultiesQuery.buildExportParams(queryParams);

	const faculties = useMemo(() => facultiesData?.data?.stats || [], [facultiesData?.data?.stats]);
	const totalCount = facultiesData?.pagination?.total || 0;

	// Sync URL params with dialog state
	useEffect(() => {
		const action = searchParams.get('action') as 'create' | 'edit' | 'view' | null;
		const facultyId = searchParams.get('id');

		// Security: Redirect unauthorized users from edit/create to view mode
		if (action && ['create', 'edit'].includes(action) && !canEdit) {
			if (facultyId) {
				setSearchParams({ action: 'view', id: facultyId });
			} else {
				setSearchParams({});
			}
			return;
		}

		if (action && ['create', 'edit', 'view'].includes(action)) {
			if (action === 'create') {
				if (!dialogRef.current.open || dialogRef.current.mode !== 'create') {
					setFacultyDialog({
						open: true,
						mode: 'create',
						faculty: null,
					});
				}
			} else if (facultyId && faculties.length > 0) {
				if (dialogRef.current.open
					&& dialogRef.current.faculty?.maKhoa === facultyId
					&& dialogRef.current.mode === action) {
					return;
				}
				const faculty = faculties.find((f) => f.maKhoa === facultyId);
				if (faculty) {
					setFacultyDialog({
						open: true,
						mode: action,
						faculty: faculty as unknown as Faculty,
					});
				}
			}
		} else if (!action && dialogRef.current.open) {
			setFacultyDialog({ open: false, mode: 'create', faculty: null });
		}
	}, [searchParams, faculties, canEdit, setSearchParams]);

	// Delete mutation
	const deleteMutation = useMutation({
		mutationFn: facultyApi.deleteFaculty,
		onSuccess: () => {
			showSuccess('Xóa khoa thành công');
			queryClient.invalidateQueries({ queryKey: ['faculties'] });
			setDeleteDialog({ open: false, faculty: null });
		},
		onError: (error: Error) => {
			showError(error.message || 'Có lỗi xảy ra khi xóa khoa');
		},
	});

	const handleRefresh = useCallback(() => {
		if (isLoading) return;
		refetch();
	}, [isLoading, refetch]);

	const handleRowClick: GridEventListener<'rowClick'> = useCallback((params) => {
		if (canEdit) {
			handleEdit(params.row as Faculty)();
		} else {
			handleView(params.row as Faculty)();
		}
	}, [canEdit, handleEdit, handleView]);

	const handleCreate = useCallback(() => {
		setSearchParams({ action: 'create' });
	}, [setSearchParams]);

	const handleDeleteConfirm = useCallback(() => {
		if (deleteMutation.isPending || !deleteDialog.faculty) return;
		deleteMutation.mutate(deleteDialog.faculty.maKhoa);
	}, [deleteDialog.faculty, deleteMutation]);

	const handleCloseFacultyDialog = useCallback(() => {
		if (searchParams.has('action')) {
			setSearchParams({});
		}
		setFacultyDialog({ open: false, mode: 'create', faculty: null });
	}, [searchParams, setSearchParams]);

	const handleCloseDeleteDialog = useCallback(() => {
		setDeleteDialog({ open: false, faculty: null });
	}, []);


	const columns = useFacultiesColumns({
		isAdmin,
		isMobile,
		facultyStats: facultiesData?.data,
		onEdit: handleEdit,
		onDelete: handleDelete
	});

	const tabs: TabConfig[] = [
		{
			label: 'Danh sách',
			content: (
				<Box sx={{
					flex: 1,
					minHeight: 0,
					height: isMobile ? LIST_HEIGHTS.MOBILE : LIST_HEIGHTS.DESKTOP,
					display: 'flex',
					flexDirection: 'column'
				}}>
					<BaseDataGrid
						rows={faculties}
						columns={columns}
						loading={isLoading}
						onRowClick={handleRowClick}
						getRowId={(row) => row.maKhoa}
						rowCount={totalCount}
						paginationModel={paginationModel}
						onPaginationModelChange={handlePaginationModelChange}
						sortModel={sortModel}
						onSortModelChange={handleSortModelChange}
						filterModel={filterModel}
						onFilterModelChange={handleFilterModelChange}
						pageSizeOptions={[10, 25, 50, 100]}
						paginationMode="server"
						sortingMode="server"
						filterMode="server"
						disableRowSelectionOnClick
						rowHeight={isMobile ? undefined : ROW_HEIGHTS.DESKTOP}
						getRowHeight={isMobile ? () => 'auto' : undefined}
						slots={{
							toolbar: () => (
								<FacultiesToolbar
									searchValue={searchValue}
									onSearch={handleSearch}
									onRefresh={handleRefresh}
									exportParams={exportParams}
									isMobile={isMobile}
								/>
							),
						}}
					/>
				</Box>
			)
		},
		{
			label: 'Biểu đồ thống kê',
			content: (
				<Box sx={{
					height: 'calc(100vh - 200px)',
					overflow: 'auto'
				}}>
					<FacultiesChart height={500} />
				</Box>
			)
		}
	];

	return (
		<Box sx={{
			width: '100%',
			height: '100vh',
			display: 'flex',
			flexDirection: 'column',
			overflow: 'hidden',
			p: 0
		}}>
			<PageHeader
				title="Danh sách Khoa"
				onRefresh={handleRefresh}
				onAdd={canEdit ? handleCreate : undefined}
				isAdmin={isAdmin}
				loading={isLoading}
			/>

			<PersistentTabs
				pageKey="faculties"
				tabs={tabs}
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					minHeight: 0
				}}
			/>

			<FacultyDialog
				open={facultyDialog.open}
				onClose={handleCloseFacultyDialog}
				faculty={facultyDialog.faculty}
				mode={facultyDialog.mode}
			/>

			<DeleteConfirmDialog
				open={deleteDialog.open}
				onClose={handleCloseDeleteDialog}
				onConfirm={handleDeleteConfirm}
				title="Xóa khoa"
				message={`Bạn có chắc chắn muốn xóa khoa "${deleteDialog.faculty?.tenKhoa}"? Hành động này không thể hoàn tác.`}
				isLoading={deleteMutation.isPending}
			/>
		</Box>
	);
}